/** What the report adds to each crown for the map: maturity and height layer. */
import type { FeatureCollection } from 'geojson';
import { inRange, TREE_EXTRAS, type LayerFilters } from '@/map/layers';
import { isMature } from './maturity';
import { stratumOf, type TreeStats } from './stats';
import type { Stratum, Tree } from './types';

export interface TreeExtras {
  mature: boolean;
  stratum: Stratum | null;
}

export function treeExtras(stats: TreeStats): Map<string, TreeExtras> {
  const out = new Map<string, TreeExtras>();
  for (const t of stats.trees) {
    out.set(t.id, {
      mature: isMature(t, stats.thresholds),
      stratum: t.height === null || !stats.strata ? null : stratumOf(t.height, stats.strata),
    });
  }
  return out;
}

export function withTreeExtras(fc: FeatureCollection, extras: Map<string, TreeExtras>): FeatureCollection {
  return {
    ...fc,
    features: fc.features.map((f, i) => {
      const x = extras.get(String(f.id ?? i + 1));
      return { ...f, properties: { ...f.properties, [TREE_EXTRAS.mature]: x?.mature ?? false, [TREE_EXTRAS.stratum]: x?.stratum ?? null } };
    }),
  };
}

/** The map's tree filter (layers.ts `treeFilter`) over normalized trees, for the selection totals. */
export function passesFilters(t: Tree, x: TreeExtras | undefined, f: LayerFilters | undefined): boolean {
  if (!f) return true;
  if (f.species?.length && !f.species.includes(t.genus)) return false;
  if (!inRange(t.height, f.height) || !inRange(t.dbhCm, f.dbh)) return false;
  if (f.strata?.length && (!x?.stratum || !f.strata.includes(x.stratum))) return false;
  if (f.matureOnly && !x?.mature) return false;
  return true;
}
